import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  fetchBoards,
  createBoard,
  updateBoard,
  deleteBoard,
  setSelectedBoard,
  UpdateBoardStatus,
} from "./redux/boardSlice";
import Popup from "../../Popup";
import { BoardCardsSkeleton } from "./Skeleton";
import showToast from "../../utils/toast";

const StateMaster = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const boards = useSelector((state) => state.boards.list);
  const loading = useSelector((state) => state.boards.loading);
  const [showPopup, setShowPopup] = useState(false);
  const [form, setForm] = useState({ id: null, name: "", description: "" });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    dispatch(fetchBoards());
  }, [dispatch]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleStatusToggle = async (id, status) => {
    const statusToSet = status === 1 ? 0 : 1;
    try {
      const res = await dispatch(
        UpdateBoardStatus({ id, status: statusToSet, updated_by: 1 }),
      ).unwrap();
      showToast.success(res?.message || "Status updated successfully");
      // setShowPopup(false);
      dispatch(fetchBoards());
      cancelEdit();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };
  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await dispatch(updateBoard(form)).unwrap();
      } else {
        await dispatch(createBoard(form)).unwrap();
      }
      setShowPopup(false);
      dispatch(fetchBoards());
      cancelEdit();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const startEdit = (board) => {
    setForm(board);
    setIsEditing(true);
    setShowPopup(true);
  };

  const cancelEdit = () => {
    setForm({ id: null, name: "", description: "" });
    setIsEditing(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this board?")) {
      try {
        await dispatch(deleteBoard(id)).unwrap();
        dispatch(fetchBoards());
      } catch (error) {
        showToast.error(error);
      }
    }
  };

  const handleBoardClick = (board) => {
    dispatch(setSelectedBoard(board));
    navigate(`/dashboard/boards/${board.id}`, {
      state: { boardName: board.name },
    });
  };

  const isFormValid = form.name.trim() && form.description.trim();

  return (
    <div
      style={{
        padding: "32px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "32px",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "8px",
            }}
          >
            Boards
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            Manage your educational boards
          </p>
        </div>
        <button
          onClick={() => {
            cancelEdit();
            setShowPopup(true);
          }}
          style={{
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.3s",
            boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = "#2563eb";
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 12px rgba(59, 130, 246, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = "#3b82f6";
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px rgba(59, 130, 246, 0.3)";
          }}
        >
          <span style={{ fontSize: "20px" }}>+</span> Add Board
        </button>
      </div>

      {/* BOARDS GRID */}
      {loading ? (
        <BoardCardsSkeleton count={6} />
      ) : boards.length > 0 ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "16px",
          }}
        >
          {boards.map((board, index) => (
            <motion.div
              key={board.id}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{
                y: -4,
                boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                borderColor: "#3b82f6",
              }}
              transition={{ duration: 0.3 }}
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                cursor: "pointer",
                position: "relative",
                border: "2px solid transparent",
              }}
            >
              {/* Board Content - Clickable Area */}
              <div onClick={() => handleBoardClick(board)}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "20px",
                    }}
                  >
                    📚
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#111827",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={board.name}
                  >
                    {board.name}
                  </h3>
                  <div
                    title={board?.status ? "Deactivate" : "Activate"}
                    onClick={(e) => {
                      e.stopPropagation(); // stop card click
                      handleStatusToggle(board?.id, board?.status);
                    }}
                    style={{
                      width: "36px",
                      height: "18px",
                      borderRadius: "9px",
                      backgroundColor: board?.status ? "#22c55e" : "#d1d5db",
                      position: "relative",
                      cursor: "pointer",
                      transition: "background-color 0.3s",
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#fff",
                        position: "absolute",
                        top: "2px",
                        left: board?.status ? "20px" : "2px",
                        transition: "left 0.3s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    />
                  </div>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#6b7280",
                    lineHeight: "1.4",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    minHeight: "36px",
                  }}
                >
                  {board.description || "No description available"}
                </p>
              </div>

              {/* Actions */}
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  marginTop: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e5e7eb",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => startEdit(board)}
                  style={{
                    flex: 1,
                    padding: "6px 12px",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#e5e7eb";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#f3f4f6";
                  }}
                >
                  <span>✏️</span> Edit
                </button>
                <button
                  onClick={() => handleDelete(board.id)}
                  style={{
                    flex: 1,
                    padding: "6px 12px",
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#fee2e2";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#fef2f2";
                  }}
                >
                  <span>🗑️</span> Delete
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>📚</div>
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#111827",
              marginBottom: "8px",
            }}
          >
            No boards yet
          </h3>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
            Click "Add Board" to create your first board
          </p>
        </div>
      )}

      {/* POPUP */}
      {showPopup && (
        <Popup
          onClose={() => {
            setShowPopup(false);
            cancelEdit();
          }}
          title={isEditing ? "Edit Board" : "Add New Board"}
          maxWidth="500px"
        >
          {/* BOARD NAME */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Board Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter board name (e.g., CBSE, ICSE)"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            />
          </div>

          {/* DESCRIPTION */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              Description <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Enter board description"
              rows="4"
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#111827",
                outline: "none",
                resize: "vertical",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
            />
          </div>

          {/* ACTIONS */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "16px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => {
                setShowPopup(false);
                cancelEdit();
              }}
              style={{
                padding: "10px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                backgroundColor: "white",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f9fafb")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "white",
                backgroundColor: isFormValid ? "#3b82f6" : "#9ca3af",
                cursor: isFormValid ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                if (isFormValid) e.target.style.backgroundColor = "#2563eb";
              }}
              onMouseOut={(e) => {
                if (isFormValid) e.target.style.backgroundColor = "#3b82f6";
              }}
            >
              {isEditing ? "Update Board" : "Save Board"}
            </button>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default StateMaster;
